/**
 * What a lead's quotable mark means, in plain words, for the lead drawer and
 * the leads table.
 *
 * Written from what the code does, not from what quotable might mean:
 *
 *   - The mark is set in WhatConverts, by a person or, for a client whose
 *     quotable write-back is switched on, by Core's write-back
 *     (backend/api/services/wc_quotable_writeback.py), which only fills in a
 *     lead nobody has marked. The AI lead scorer (wc_ai_score.py) is
 *     shadow-only: it labels leads inside Core for comparison and never
 *     writes to WhatConverts. So an unmarked lead is not waiting on the
 *     scorer; nobody has marked it yet.
 *   - On this page, only a quotable lead with a Google click ID is shown as
 *     ready for Google Ads; a not-quotable lead is shown as Skipped and left
 *     out of the quotable counts (WhatConvertsLeadsPage.tsx, convState).
 *   - WhatConverts gives no free-text reason on the leads read. It does send
 *     spam and duplicate flags (normalize_lead in whatconverts_service.py),
 *     so those are shown when set.
 *
 * Deliberately NOT claimed: what Google Ads itself receives. Sending
 * conversions to Google is moving to WhatConverts's own integration, so a
 * sentence about Core's upload would be untrue on some deployments.
 */
import type { WcLead } from '@/services/whatconvertsService';

type QuotableFields = Pick<WcLead, 'quotable'> & {
  spam?: boolean | null;
  duplicate?: boolean | null;
};

/** The reason WhatConverts itself gives, when it gives one. */
export function wcFlagReason(lead: QuotableFields): string | null {
  if (lead.spam && lead.duplicate) {
    return 'WhatConverts flagged it as spam and as a duplicate of an earlier lead.';
  }
  if (lead.spam) return 'WhatConverts flagged it as spam.';
  if (lead.duplicate) return 'WhatConverts flagged it as a duplicate of an earlier lead.';
  return null;
}

/** Short label for the leads table's Google Ads column, shared with the
 *  drawer note so the two always say the same thing. */
export const NEEDS_FOLLOW_UP = 'Needs follow-up';
export const NOT_MARKED_YET = 'Not marked yet';

/** One or two sentences for a lead that is not marked quotable; null for a quotable one. */
export function quotableExplanation(lead: QuotableFields): string | null {
  switch (lead.quotable) {
    case 'no':
      return 'Marked as not a real prospect (for example spam, a wrong number or a sales call), so it is left out of the quotable count and skipped for Google Ads. If that is wrong, change the mark in WhatConverts and Core picks it up on the next read.';
    case 'pending':
      return `${NEEDS_FOLLOW_UP}: marked pending because it is not clear yet whether this is a real prospect. Until it is marked quotable, it is not counted as quotable or ready for Google Ads.`;
    case 'not_set':
      return `${NOT_MARKED_YET}: nobody has marked this lead in WhatConverts. Until it is marked quotable, it is not counted as quotable or ready for Google Ads.`;
    default:
      return null;
  }
}

/** The full note (WhatConverts's reason first, when it gives one), or null for a quotable lead. */
export function quotableNote(lead: QuotableFields): string | null {
  const text = quotableExplanation(lead);
  if (!text) return null;
  const reason = wcFlagReason(lead);
  return reason ? `${reason} ${text}` : text;
}
